import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { getNotifications, getUnreadCount, markAllRead, markRead } from '../../api/notifications';
import type { Notification } from '../../types/mjai';

const POLL_INTERVAL = 30_000; // 30 seconds

const TYPE_LABELS: Record<string, string> = {
  mention: 'があなたをメンションしました',
  reply: 'があなたのコメントに返信しました',
  vote_on_my_hand: 'があなたの局面にコメントしました',
  tip_received: 'から投げ銭を受け取りました',
  comment_like: 'があなたのコメントにいいねしました',
};

export default function NotificationBell() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const fetchUnread = useCallback(async () => {
    if (!user) return;
    try {
      const { count } = await getUnreadCount();
      setUnreadCount(count);
    } catch {}
  }, [user]);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await getNotifications();
      setNotifications(data);
      setUnreadCount(data.filter((n) => !n.is_read).length);
    } catch {} finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    fetchUnread();
    const timer = setInterval(fetchUnread, POLL_INTERVAL);
    return () => clearInterval(timer);
  }, [user, fetchUnread]);

  useEffect(() => {
    if (!open) return;
    fetchNotifications();
  }, [open, fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleMarkAllRead = async () => {
    await markAllRead();
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnreadCount(0);
  };

  const handleClickNotification = async (n: Notification) => {
    if (!n.is_read) {
      await markRead(n.id);
      setNotifications((prev) =>
        prev.map((notif) => (notif.id === n.id ? { ...notif, is_read: true } : notif))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    }
    if (n.hand_id) {
      queryClient.invalidateQueries({ queryKey: ['comments', n.hand_id] });
    }
    setOpen(false);
  };

  if (!user) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-1.5 text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100"
        aria-label="通知"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-5 h-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold leading-none">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-9 w-80 bg-white border rounded-xl shadow-lg z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 border-b bg-gray-50">
            <span className="font-semibold text-sm text-gray-800">通知</span>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-blue-500 hover:underline"
              >
                すべて既読
              </button>
            )}
          </div>

          {loading ? (
            <div className="p-4 text-center text-sm text-gray-400">読み込み中...</div>
          ) : notifications.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-400">通知はありません</div>
          ) : (
            <ul className="max-h-80 overflow-y-auto divide-y">
              {notifications.map((n) => (
                <li key={n.id}>
                  {n.hand_id ? (
                    <Link
                      to={`/hands/${n.hand_id}`}
                      onClick={() => handleClickNotification(n)}
                      className={`flex gap-2 px-4 py-3 hover:bg-gray-50 ${
                        !n.is_read ? 'bg-blue-50' : ''
                      }`}
                    >
                      <NotificationItem notification={n} />
                    </Link>
                  ) : (
                    <button
                      onClick={() => handleClickNotification(n)}
                      className={`flex gap-2 px-4 py-3 hover:bg-gray-50 w-full text-left ${
                        !n.is_read ? 'bg-blue-50' : ''
                      }`}
                    >
                      <NotificationItem notification={n} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function NotificationItem({ notification: n }: { notification: Notification }) {
  const actorName = n.actor ? n.actor.x_display_name : '誰か';
  const label = TYPE_LABELS[n.type] ?? 'からの通知';
  const time = new Date(n.created_at).toLocaleDateString('ja-JP', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <>
      {n.actor?.x_avatar_url ? (
        <img src={n.actor.x_avatar_url} alt="" className="w-8 h-8 rounded-full shrink-0" />
      ) : (
        <div className="w-8 h-8 rounded-full bg-gray-200 shrink-0 flex items-center justify-center text-xs text-gray-500">
          {actorName[0]}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-700 leading-snug">
          <span className="font-semibold">{actorName}</span>
          {label}
        </p>
        <p className="text-xs text-gray-400 mt-0.5">{time}</p>
      </div>
      {!n.is_read && (
        <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0 mt-1" />
      )}
    </>
  );
}
